import { useAuthActions } from "@convex-dev/auth/react";
import { useAction, useMutation, useQuery } from "convex/react";
import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { api } from "../../convex/_generated/api";
import { ConvexError } from "convex/values";
import { Button, Checkbox, Field, Input, Select, cn, errMsg } from "@/components/ui";
import { LogoMark } from "../components/Logo";
import { PRICE_PER_SEAT, pendingSignup, siteLink } from "@/lib/site";

// The sign-in, sign-up and reset screens sit outside the signed-in app's routes, so they move with a plain page load.
const go = (path: string) => window.location.assign(path);

function Card({ children, onSubmit, wide }: { children: React.ReactNode; onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void; wide?: boolean }) {
  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <form onSubmit={onSubmit} className={cn("w-full space-y-4 rounded-lg border border-slate-200 bg-surface p-6 shadow-sm", wide ? "max-w-md" : "max-w-sm")}>
        {children}
      </form>
    </div>
  );
}

function Brand({ sub = "Earned value and progress for EPC projects" }: { sub?: string }) {
  return (
    <div>
      <h1 className="mb-1 flex items-center gap-2.5 text-xl font-semibold">
        <LogoMark size={32} />
        Mongoose EVM
      </h1>
      <p className="text-sm text-slate-500">{sub}</p>
    </div>
  );
}

export function SignIn() {
  const { signIn } = useAuthActions();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Card
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        const fd = new FormData(e.currentTarget);
        fd.set("flow", "signIn");
        try {
          await signIn("password", fd);
        } catch (err) {
          // Production redacts server errors to "Called by client" unless they're ConvexErrors.
          setError(err instanceof ConvexError ? String(err.data) : "That email and password don't match an account. New here? Create an account below.");
        } finally {
          setBusy(false);
        }
      }}
    >
      <Brand />
      <Field label="Email">
        <Input name="email" type="email" autoComplete="email" required />
      </Field>
      <Field label="Password">
        <Input name="password" type="password" autoComplete="current-password" required minLength={8} />
      </Field>
      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
      <Button variant="primary" className="w-full" busy={busy} type="submit">
        Sign in
      </Button>
      <div className="flex justify-between text-sm text-slate-500">
        <button type="button" className="text-brand-600 underline" onClick={() => go("/reset")}>
          Forgot password?
        </button>
        <span>
          New here?{" "}
          <button type="button" className="text-brand-600 underline" onClick={() => go("/signup")}>
            Create an account
          </button>
        </span>
      </div>
    </Card>
  );
}

const FEATURES = ["Deliverable tracking", "Milestone rules of credit", "Planned vs. earned progress", "Progress reporting and Excel export"];

/**
 * Sign-up (Cesar's Oct 18 page concept): account details plus the number of users. With billing on, the next step is
 * Stripe Checkout; the organisation is created right after the account (see Onboarding), then Checkout opens.
 */
export function SignUp() {
  const { signIn } = useAuthActions();
  const cfg = useQuery(api.billing.publicConfig);
  const initialSeats = Math.max(1, Math.min(500, Number(new URLSearchParams(window.location.search).get("users")) || 1));
  const [seats, setSeats] = useState(initialSeats);
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const billing = !!cfg?.enabled;
  const price = cfg?.pricePerSeat ?? PRICE_PER_SEAT;
  const steps = billing ? ["Create account", "Secure payment", "Open Mongoose"] : ["Create account", "Open Mongoose"];
  return (
    <div className="min-h-full overflow-y-auto p-6">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between">
          <a href={siteLink("")} className="flex items-center gap-2.5 text-lg font-semibold">
            <LogoMark size={30} /> Mongoose EVM
          </a>
          <span className="text-sm text-slate-500">
            Already have an account?{" "}
            <button type="button" className="text-brand-600 underline" onClick={() => go("/")}>
              Log in
            </button>
          </span>
        </div>
        <h1 className="mt-8 text-center text-3xl font-semibold">Get started with Mongoose EVM</h1>
        <ol className="mt-4 flex flex-wrap items-center justify-center gap-3 text-sm">
          {steps.map((s, i) => (
            <li key={s} className="flex items-center gap-2">
              <span className={cn("flex size-7 items-center justify-center rounded-full text-sm font-semibold", i === 0 ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-500")}>{i + 1}</span>
              <span className={i === 0 ? "font-medium" : "text-slate-500"}>{s}</span>
              {i < steps.length - 1 && <span className="text-slate-400">→</span>}
            </li>
          ))}
        </ol>
        <div className="mt-8 grid gap-6 md:grid-cols-[3fr_2fr]">
          <form
            className="space-y-4 rounded-lg border border-slate-200 bg-surface p-6 shadow-sm"
            onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              const fd = new FormData(e.currentTarget);
              const company = String(fd.get("company") ?? "").trim();
              if (!terms) return setError("Please accept the Terms of Service and Privacy Policy");
              setBusy(true);
              pendingSignup.set({ company, seats, acceptTerms: true });
              fd.delete("company");
              fd.set("flow", "signUp");
              try {
                await signIn("password", fd);
                // Signed in: the app moves on to Onboarding, which creates the organisation (and opens Checkout).
              } catch (err) {
                pendingSignup.clear();
                const m = err instanceof ConvexError ? String(err.data) : errMsg(err);
                setError(
                  err instanceof ConvexError
                    ? m
                    : /already exists/i.test(m)
                    ? "That email already has an account. Log in instead."
                    : "Couldn't create the account. That email may already be registered; try logging in."
                );
                setBusy(false);
              }
            }}
          >
            <h2 className="text-xl font-semibold">Create your account</h2>
            <Field label="Full name">
              <Input name="name" autoComplete="name" required />
            </Field>
            <Field label="Work email">
              <Input name="email" type="email" autoComplete="email" placeholder="you@company.com" required />
            </Field>
            <Field label="Company" hint="Joining a team that invited you? Leave this blank.">
              <Input name="company" autoComplete="organization" />
            </Field>
            <Field label="Password" hint="At least 8 characters">
              <Input name="password" type="password" autoComplete="new-password" required minLength={8} />
            </Field>
            <Checkbox
              label={
                <>
                  I agree to the{" "}
                  <a className="text-brand-600 underline" href={siteLink("terms")} target="_blank" rel="noreferrer">
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a className="text-brand-600 underline" href={siteLink("privacy")} target="_blank" rel="noreferrer">
                    Privacy Policy
                  </a>
                  .
                </>
              }
              checked={terms}
              onChange={setTerms}
            />
            {error && (
              <p role="alert" className="text-sm text-red-600">
                {error}
              </p>
            )}
            <Button variant="primary" className="w-full" busy={busy} type="submit">
              {billing ? "Continue to secure checkout →" : "Create account"}
            </Button>
            {billing && <p className="text-center text-xs text-slate-500">Payment details are entered securely on Stripe.</p>}
          </form>
          <aside className="space-y-4 rounded-lg border border-slate-200 bg-brand-50/40 p-6">
            {/* Until billing is switched on there's nothing to choose or pay: just what's included. */}
            {billing && (
              <>
                <h2 className="text-xl font-semibold">Your subscription</h2>
                <div className="flex items-start justify-between border-t border-slate-200 pt-4">
                  <div>
                    <p className="font-medium">Mongoose EVM</p>
                    <p className="text-sm text-slate-500">Monthly subscription</p>
                  </div>
                  <p>
                    <b>${price}</b> per user / month
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <label htmlFor="seats" className="text-sm">
                    Users
                  </label>
                  <Select id="seats" aria-label="Users" className="w-24" value={seats} onChange={(e) => setSeats(Number(e.target.value))}>
                    {[...Array(50)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="border-t border-slate-200 pt-4">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <b>${(seats * price).toFixed(2)} / month</b>
                  </div>
                  <p className="text-xs text-slate-500">Applicable taxes calculated at checkout.</p>
                  {billing && cfg.trialDays > 0 && (
                    <p className="mt-2 text-sm font-medium text-brand-700">Free for {cfg.trialDays} days. You won't be charged until the trial ends; cancel any time before.</p>
                  )}
                </div>
                <p className="border-t border-slate-200 pt-4 text-xs text-slate-500">Renews monthly. You can add users or cancel future renewals in Account &amp; billing.</p>
              </>
            )}
            <div>
              <p className="mb-2 font-medium">Included</p>
              <ul className="space-y-1.5 text-sm">
                {FEATURES.map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <Check className="size-4 text-brand-600" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
        <p className="mt-6 text-center text-xs text-slate-500">
          Need help?{" "}
          <a className="text-brand-600 underline" href={siteLink("help")}>
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
}

/** Forgot password: an 8-digit code by email, then a new password. */
export function ResetPassword() {
  const { signIn } = useAuthActions();
  const [step, setStep] = useState<"email" | { email: string }>("email");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const fd = new FormData(e.currentTarget);
    try {
      if (step === "email") {
        fd.set("flow", "reset");
        await signIn("password", fd);
        setStep({ email: String(fd.get("email")) });
      } else {
        fd.set("flow", "reset-verification");
        fd.set("email", step.email);
        await signIn("password", fd);
        go("/");
      }
    } catch (err) {
      setError(
        err instanceof ConvexError
          ? String(err.data)
          : step === "email"
          ? "Couldn't send a code to that email. Check the address, or contact support."
          : "That code didn't work. Check it, or ask for a new one."
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card onSubmit={submit}>
      <Brand sub="Reset your password" />
      {step === "email" ? (
        <>
          <p className="text-sm text-slate-600">Enter your account's email. We'll send a code to reset your password.</p>
          <Field label="Email">
            <Input name="email" type="email" autoComplete="email" required />
          </Field>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-600">
            We sent a code to <b>{step.email}</b>. It expires in 15 minutes.
          </p>
          <Field label="Code">
            <Input name="code" inputMode="numeric" autoComplete="one-time-code" required />
          </Field>
          <Field label="New password" hint="At least 8 characters">
            <Input name="newPassword" type="password" autoComplete="new-password" required minLength={8} />
          </Field>
        </>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
      <Button variant="primary" className="w-full" busy={busy} type="submit">
        {step === "email" ? "Send code" : "Set new password"}
      </Button>
      <p className="text-center text-sm text-slate-500">
        <button type="button" className="text-brand-600 underline" onClick={() => go("/")}>
          Back to sign in
        </button>
      </p>
    </Card>
  );
}

export function Onboarding({ invited }: { invited: boolean }) {
  const bootstrap = useMutation(api.account.bootstrap);
  const checkout = useAction(api.billing.startCheckout);
  const cfg = useQuery(api.billing.publicConfig);
  const { signOut } = useAuthActions();
  const pending = useRef(pendingSignup.get());
  const [name, setName] = useState(pending.current?.company ?? "");
  const [seats, setSeats] = useState(pending.current?.seats ?? 1);
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const billing = !!cfg?.enabled;
  const create = async (p: { company: string; seats: number; acceptTerms: boolean }) => {
    setBusy(true);
    setError(null);
    try {
      if (invited) await bootstrap({});
      else {
        await bootstrap({
          companyName: p.company,
          seats: p.seats,
          acceptTerms: p.acceptTerms || undefined,
        });
        pendingSignup.clear();
        // Billing on: straight on to Stripe Checkout (the app shows the subscription screen until it's done).
        if (billing) window.location.href = await checkout({ seats: p.seats });
      }
    } catch (e) {
      setError(errMsg(e));
      setBusy(false);
    }
  };
  // Coming from the sign-up page: everything is known, so carry on without asking again.
  const started = useRef(false);
  useEffect(() => {
    if (started.current || invited || !pending.current?.company || cfg === undefined) return;
    started.current = true;
    void create(pending.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg, invited]);
  if (!invited && pending.current?.company && !error)
    return (
      <div className="flex min-h-full items-center justify-center p-6 text-sm text-slate-600">
        {billing ? "Setting up your organisation and opening secure checkout..." : "Setting up your organisation..."}
      </div>
    );
  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-4 rounded-lg border border-slate-200 bg-surface p-6 shadow-sm">
        {invited ? (
          <>
            <h1 className="text-lg font-semibold">You've been invited</h1>
            <p className="text-sm text-slate-600">Join the organisation that invited you.</p>
          </>
        ) : (
          <>
            <h1 className="text-lg font-semibold">Set up your organisation</h1>
            <Field label="Company name">
              <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </Field>
            {billing && (
              <>
                <Field label="Users" hint={`$${cfg.pricePerSeat} per user per month${cfg.trialDays ? `, free for the first ${cfg.trialDays} days` : ""}`}>
                  <Input type="number" min={1} max={500} value={seats} onChange={(e) => setSeats(Math.max(1, Number(e.target.value) || 1))} />
                </Field>
                <Checkbox
                  label={
                    <>
                      I agree to the{" "}
                      <a className="text-brand-600 underline" href={siteLink("terms")} target="_blank" rel="noreferrer">
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a className="text-brand-600 underline" href={siteLink("privacy")} target="_blank" rel="noreferrer">
                        Privacy Policy
                      </a>
                    </>
                  }
                  checked={terms}
                  onChange={setTerms}
                />
              </>
            )}
          </>
        )}
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <div className="flex justify-between">
          <Button variant="ghost" onClick={() => void signOut()}>
            Sign out
          </Button>
          <Button variant="primary" busy={busy} onClick={() => void create({ company: name, seats, acceptTerms: terms })} disabled={!invited && (!name.trim() || (billing && !terms))}>
            {invited ? "Join" : billing ? "Continue to checkout" : "Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}
